import { AppId, RichAppIconData } from '@inno-tools/container-bridge'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface TopDockStore {
  pinnedAppIds: AppId[]
  disableRichAppIconsAppIds: AppId[]
  richAppIconDataMap: Record<string, RichAppIconData>
  setPinnedAppIds: (appIds: AppId[]) => void
  addPinnedAppId: (appId: AppId) => void
  removePinnedAppId: (appId: AppId) => void
  enableRichAppIcon: (appId: AppId) => void
  disableRichAppIcon: (appId: AppId) => void
  isRichAppIconDisabled: (appId?: AppId) => boolean
  getRichAppIconData: (appId?: AppId) => RichAppIconData | null
  setRichAppIconData: (appId: AppId, data: RichAppIconData | null) => void
  isRichAppIconVisible: (appId?: AppId) => boolean
  toggleRichAppIcon: (appId?: AppId) => void
}

export const defaultTopDocksApps: AppId[] = [
  AppId.CLUSTER_FILES,
  AppId.TASK_MANAGEMENT,
  AppId.TIMETRACKING,
  AppId.CALENDAR,
  AppId.LIVE_CHAT,
  AppId.EMAIL
]

export const useTopDockStore = create<TopDockStore>()(
  persist(
    (set, get) => ({
      pinnedAppIds: defaultTopDocksApps,
      disableRichAppIconsAppIds: [],
      richAppIconDataMap: {},
      setPinnedAppIds: appIds => set({ pinnedAppIds: appIds }),
      addPinnedAppId: appId =>
        set(state =>
          state.pinnedAppIds.includes(appId)
            ? state
            : { pinnedAppIds: [...state.pinnedAppIds, appId] }
        ),
      removePinnedAppId: appId =>
        set(state => ({
          pinnedAppIds: state.pinnedAppIds.filter(id => id !== appId)
        })),
      enableRichAppIcon: appId =>
        set(state => ({
          disableRichAppIconsAppIds: state.disableRichAppIconsAppIds.filter(
            id => id !== appId
          )
        })),
      disableRichAppIcon: appId =>
        set(state =>
          state.disableRichAppIconsAppIds.includes(appId)
            ? state
            : {
                disableRichAppIconsAppIds: [
                  ...state.disableRichAppIconsAppIds,
                  appId
                ]
              }
        ),
      isRichAppIconDisabled: appId =>
        !appId ? false : get().disableRichAppIconsAppIds.includes(appId),
      getRichAppIconData: appId =>
        !appId ? null : (get().richAppIconDataMap[String(appId)] ?? null),
      setRichAppIconData: (appId, data) =>
        set(state => {
          if (data === null) {
            const { [String(appId)]: _, ...rest } = state.richAppIconDataMap
            return { richAppIconDataMap: rest }
          }
          return {
            richAppIconDataMap: {
              ...state.richAppIconDataMap,
              [String(appId)]: data
            }
          }
        }),
      isRichAppIconVisible: appId =>
        !appId ? false : String(appId) in get().richAppIconDataMap,
      toggleRichAppIcon: appId => {
        if (!appId) return
        const state = get()
        if (state.disableRichAppIconsAppIds.includes(appId)) {
          state.enableRichAppIcon(appId)
        } else {
          state.disableRichAppIcon(appId)
        }
      }
    }),
    {
      name: LocalStorageKey.DISABLED_RICH_APP_ICONS,
      partialize: state => ({
        disableRichAppIconsAppIds: state.disableRichAppIconsAppIds
      })
    }
  )
)
